import React, { useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { Edit as EditIcon, Mail, Visibility, Delete as DeleteIcon } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import BulkMessageDynamicFormModal from "./BulkMessageDynamicFormModal";

const FormsList = ({ forms, loading, onViewForm, onEditMetadata, onDeleteForm }) => {
  const [bulkMessageOpen, setBulkMessageOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  let content;

  const handleOpenBulkMessage = (form) => {
    setSelectedForm(form);
    setBulkMessageOpen(true);
  };

  const handleCloseBulkMessage = () => {
    setBulkMessageOpen(false);
    setSelectedForm(null);
  };

  if (loading) {
    content = (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <CircularProgress />
      </Box>
    );
  } else if (forms.length === 0) {
    content = (
      <Typography variant="body1" sx={{ textAlign: "center", my: 4 }}>
        No forms found. Click the + button to add a new form.
      </Typography>
    );
  } else {
    content = (
      <List>
        {forms.map((form) => (
          <ListItem key={form.id} divider>
            <ListItemText
              primary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="subtitle1">{form.name}</Typography>
                  {form.metadata?.status && (
                    <Chip
                      size="small"
                      label={form.metadata.status}
                      color={form.metadata.status === "Active" ? "success" : "error"}
                      sx={{ height: 24 }}
                    />
                  )}
                </Box>
              }
              secondary={
                <>
                  <Typography variant="body2">
                    Original file: {form.originalFilename} | Created:{" "}
                    {moment(form.createdAt).format("MM-DD-YYYY hh:mm A")}
                  </Typography>
                  {form.metadata && (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="caption" component="span" sx={{ mr: 2 }}>
                        <strong>Short Name:</strong> {form.metadata.shortName}
                      </Typography>
                      <Typography variant="caption" component="span" sx={{ mr: 2 }}>
                        <strong>Visibility:</strong> {form.metadata.visibility}
                      </Typography>
                      <Typography variant="caption" component="span" sx={{ mr: 2 }}>
                        <strong>Onboarding:</strong> {form.metadata.onboarding}
                      </Typography>
                      {form.metadata.onboarding === "Yes" && form.metadata.minOnboardingStage && (
                        <Typography variant="caption" component="span" sx={{ mr: 2 }}>
                          <strong>Min. Stage:</strong> {form.metadata.minOnboardingStage}
                        </Typography>
                      )}
                      {form.metadata.onboarding === "Yes" && form.metadata.required !== undefined && (
                        <Typography variant="caption" component="span" sx={{ mr: 2 }}>
                          <strong>Required:</strong> {form.metadata.required}
                        </Typography>
                      )}
                    </Box>
                  )}
                </>
              }
            />
            <ListItemSecondaryAction sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Bulk Message">
                <IconButton
                  edge="end"
                  onClick={() => handleOpenBulkMessage(form)}
                  color="error"
                >
                  <Mail />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit metadata">
                <IconButton
                  edge="end"
                  onClick={() => onEditMetadata && onEditMetadata(form.id, form.metadata, form.name)}
                  color="info"
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="View all form fields">
                <IconButton edge="end" onClick={() => onViewForm(form.id)}>
                  <Visibility />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete form">
                <IconButton
                  edge="end"
                  onClick={() => onDeleteForm && onDeleteForm(form.id, form.name, form.metadata?.shortName)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    );
  }

  return (
    <Box>
      {content}
      {selectedForm && (
        <BulkMessageDynamicFormModal
          open={bulkMessageOpen}
          onClose={handleCloseBulkMessage}
          formName={selectedForm.name}
          formId={selectedForm.id}
        />
      )}
    </Box>
  );
};

FormsList.propTypes = {
  forms: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  onViewForm: PropTypes.func.isRequired,
  onEditMetadata: PropTypes.func,
  onDeleteForm: PropTypes.func,
};

FormsList.defaultProps = {
  onEditMetadata: null,
  onDeleteForm: null,
};

export default FormsList;
